import React from 'react';
import { StepperWrap, Step, Dot, Label, StepLine } from './stepper.styles';

/*
  Generic stepper shared across Upload -> Verify -> Results -> Assessment Report.

  Now purely state-driven (no react-router) — the parent owns which step is
  active and hands down `onNavigate(stepKey)`, so switching steps never
  unmounts/remounts anything and no in-progress data is lost.

  Props:
   - current: 'upload' | 'verify' | 'results' | 'report | reports'
   - onNavigate(stepKey): called when the user clicks an earlier, already-
     reached step. Steps ahead of `current` are not clickable (can't skip
     ahead), matching the HTML prototype's onclick guards.
   - minAllowed: index below which steps are locked — already completed
     before this session started (resumed flow) or lost to a refresh
     (normal flow). Locked steps render as done but aren't clickable.
     Optional; defaults to 0 (no lock), matching current behavior.
*/

const STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'verify', label: 'Verification' },
  { key: 'results', label: 'Results' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'reports', label: 'Reports' },
];

const Stepper = ({ current, onNavigate, minAllowed = 0 }) => {
  if (!current) return null;

  const curIdx = STEPS.findIndex((s) => s.key === current);

  return (
    <StepperWrap>
      {STEPS.map((step, idx) => {
        const done = idx < curIdx;
        const active = idx === curIdx;
        const locked = idx < minAllowed;
        const clickable = done && !locked && !!onNavigate;
        return (
          <React.Fragment key={step.key}>
            <Step $clickable={clickable} onClick={() => clickable && onNavigate(step.key)}>
              <Dot $done={done} $active={active}>
                {done ? '✓' : idx + 1}
              </Dot>
              <Label $done={done} $active={active}>
                {step.label}
              </Label>
            </Step>
            {idx < STEPS.length - 1 && <StepLine $done={idx < curIdx} />}
          </React.Fragment>
        );
      })}
    </StepperWrap>
  );
};

export default Stepper;
